import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import FormInputField from "@/components/lib/from-input-field";
import { Form } from "@/components/ui/form";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImageCropUploader from "@/components/common/image-crop-uploader";
import FileUploader from "@/components/common/file-uploader";
import useAuth from "@/hooks/useAuth";
import { editLoyaltyTemplate, addLoyaltyTemplate } from "@/redux/slice/loyalty/templeteSlice";

const templateSchema = z.object({
  title: z.string().min(2, "Title must be at least 2 characters").max(100),
});

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
];

const MAX_IMAGE_SIZE_BYTES = 1024 * 1024;
const TEMPLATE_IMAGE_DIMENSION = { width: 546, height: 262 };
const TEMPLATE_FILE_UPLOADER_INPUT_ID = "gift-card-template-file-uploader";
const LOGO_FILE_UPLOADER_INPUT_ID = "gift-card-logo-file-uploader";

const validateImageFile = async ({ file, fieldLabel }) => {
  if (!file) return `${fieldLabel} is required.`;

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return `${fieldLabel} must be jpeg, png, jpg, gif, or webp.`;
  }

  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return `${fieldLabel} must be within 1MB.`;
  }

  return null;
};

export function EditGiftCardDialog({
  isOpen,
  onClose,
  type,
  restaurantId,
  selectedTemplate,
}) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { createLoading, updateLoading } = useSelector(
    (state) => state.loyalty.template,
  );
  const [templateUploadedFiles, setTemplateUploadedFiles] = useState([]);
  const [logoUploadedFiles, setLogoUploadedFiles] = useState([]);
  const [templateFileManagerId, setTemplateFileManagerId] = useState(null);
  const [logoFileManagerId, setLogoFileManagerId] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");

  const isEditMode = Boolean(selectedTemplate?.id);
  const isLogoRequiredType = type === "c2c_voucher" || type === "gift_card";
  const isSubmitting = createLoading || updateLoading;
  const resolvedRestaurantId = restaurantId || user?.restaurant_id || 1;
  const resolvedClientId = user?.client_id || 1;
  const uploadDirectory = `restaurant/${resolvedRestaurantId}/loyalty-template/image`;

  const form = useForm({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      title: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    form.reset({
      title: selectedTemplate?.title || "",
    });
    setTemplateUploadedFiles([]);
    setLogoUploadedFiles([]);
    setTemplateFileManagerId(null);
    setLogoFileManagerId(null);
    setLogoPreviewUrl("");
  }, [isOpen, selectedTemplate, form]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const handleClose = (openState) => {
    if (!openState) {
      onClose();
    }
  };

  const triggerUploaderWithFile = ({ inputId, file, errorLabel }) => {
    const uploaderInput = document.getElementById(inputId);
    if (!uploaderInput) {
      toast.error(`Could not find ${errorLabel} uploader.`);
      return;
    }

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    uploaderInput.files = dataTransfer.files;
    uploaderInput.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const handleTemplateCropChange = (file) => {
    if (!file) return;
    triggerUploaderWithFile({
      inputId: TEMPLATE_FILE_UPLOADER_INPUT_ID,
      file,
      errorLabel: "template image",
    });
  };

  const handleTemplateUploadComplete = (uploadedFiles) => {
    const uploadedId =
      uploadedFiles?.[0]?.file_id || uploadedFiles?.[0]?.file_manager_id || null;
    setTemplateFileManagerId(uploadedId);
  };

  const handleLogoUploadComplete = (uploadedFiles) => {
    const uploadedId =
      uploadedFiles?.[0]?.file_id || uploadedFiles?.[0]?.file_manager_id || null;
    setLogoFileManagerId(uploadedId);
  };

  const onSubmit = async (data) => {
    if (!restaurantId || !type) {
      toast.error("Missing required context: restaurant or template type.");
      return;
    }

    const payload = new FormData();
    payload.append("restaurant_id", String(restaurantId));
    payload.append("type", type);
    payload.append("title", data.title.trim());

    if (templateFileManagerId) {
      payload.append("file_manager_id", String(templateFileManagerId));
    } else if (!isEditMode) {
      toast.error("Please upload an image.");
      return;
    }

    if (isLogoRequiredType) {
      if (logoFileManagerId) {
        payload.append("logo_file_manager_id", String(logoFileManagerId));
      } else if (!isEditMode) {
        toast.error("Please upload a logo image.");
        return;
      }
    }

    try {
      if (isEditMode) {
        await dispatch(
          editLoyaltyTemplate({
            template_id: selectedTemplate.id,
            data: payload,
          }),
        ).unwrap();
        toast.success("Template updated successfully.");
      } else {
        await dispatch(addLoyaltyTemplate(payload)).unwrap();
        toast.success("Template created successfully.");
      }

      onClose();
    } catch (error) {
      toast.error(error || "Failed to save template.");
    }
  };

  const handleLogoSelect = async (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    const fileError = await validateImageFile({
      file,
      fieldLabel: "Logo image",
    });

    if (fileError) {
      toast.error(fileError);
      return;
    }

    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(URL.createObjectURL(file));

    triggerUploaderWithFile({
      inputId: LOGO_FILE_UPLOADER_INPUT_ID,
      file,
      errorLabel: "logo image",
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col">
        <div className="py-5 flex flex-col min-h-0">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            {isEditMode ? "Update Template" : "Create Template"}
          </DialogTitle>
          <Separator className="mt-4" />
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6 mt-5 overflow-y-auto pr-2 pb-1 min-h-0"
            >
              <FormInputField
                name="title"
                style="bg-cblack6"
                placeholder={"Enter your title"}
                form={form}
                label={"Title"}
                type="text"
              />

              <ImageCropUploader
                label="Template Image (max 1MB, 546 x 262 px)"
                outputDimension={TEMPLATE_IMAGE_DIMENSION}
                onCroppedFileChange={handleTemplateCropChange}
                onError={(message) => toast.error(message)}
              />

              <div className="hidden">
                <FileUploader
                  name={TEMPLATE_FILE_UPLOADER_INPUT_ID}
                  files={templateUploadedFiles}
                  onFilesChange={setTemplateUploadedFiles}
                  onUploadComplete={handleTemplateUploadComplete}
                  multiple={false}
                  replace={true}
                  clientId={resolvedClientId}
                  restaurantId={resolvedRestaurantId}
                  directory={uploadDirectory}
                  label="Template file uploader"
                />
              </div>

              {isLogoRequiredType && (
                <>
                  <div className="space-y-2">
                    <Label className="text-cwhite">Logo Image (max 1MB)</Label>
                    <Input
                      type="file"
                      accept=".jpeg,.jpg,.png,.gif,.webp"
                      className="text-cwhite file:text-cwhite file:font-medium"
                      onChange={handleLogoSelect}
                    />
                  </div>

                  {logoPreviewUrl ? (
                    <div className="space-y-2">
                      <p className="text-sm text-cwhite2">Logo preview</p>
                      <img
                        src={logoPreviewUrl}
                        alt="Selected logo preview"
                        className="h-24 w-24 rounded object-cover border border-cblack6"
                      />
                    </div>
                  ) : null}

                  <div className="hidden">
                    <FileUploader
                      name={LOGO_FILE_UPLOADER_INPUT_ID}
                      files={logoUploadedFiles}
                      onFilesChange={setLogoUploadedFiles}
                      onUploadComplete={handleLogoUploadComplete}
                      multiple={false}
                      replace={true}
                      clientId={resolvedClientId}
                      restaurantId={resolvedRestaurantId}
                      directory={uploadDirectory}
                      label="Logo file uploader"
                    />
                  </div>
                </>
              )}

              <Button isLoading={isSubmitting} className="bg-cyellow w-full text-cblack">
                {isEditMode ? "Update" : "Create"}
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
